import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { type CompanyClassSummary, type EmployerRef } from "@vidario/domain";

import { useVidarioClient } from "../client";
import { queryKeys } from "../queryKeys";

const COMPANIES = ["companies"] as const;

function useCompaniesMutation<TInput, TResult>(
  fn: (input: TInput) => Promise<TResult>,
  /** A query the change made obsolete: dropped instead of refetched. */
  gone?: (input: TInput) => readonly unknown[],
): UseMutationResult<TResult, Error, TInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    // Assignments move rows between the to-do list, classes and counts. Not
    // awaited: the caller's own callbacks run at once, before a refetch can
    // unmount the component that started the change.
    onSettled: (_result, error, input) => {
      const obsolete = !error && gone ? JSON.stringify(gone(input)) : null;
      if (obsolete) qc.removeQueries({ queryKey: gone?.(input) ?? [], exact: true });
      void qc.invalidateQueries({
        queryKey: COMPANIES,
        predicate: (query) => JSON.stringify(query.queryKey) !== obsolete,
      });
    },
  });
}

export function useCreateCompanyClass(): UseMutationResult<CompanyClassSummary, Error, string> {
  const client = useVidarioClient();
  return useCompaniesMutation((name: string) => client.createCompanyClass(name));
}

export function useRenameCompanyClass(): UseMutationResult<CompanyClassSummary, Error, { id: number; name: string }> {
  const client = useVidarioClient();
  return useCompaniesMutation((input: { id: number; name: string }) => client.renameCompanyClass(input));
}

export function useDeleteCompanyClass(): UseMutationResult<void, Error, number> {
  const client = useVidarioClient();
  // The deleted class's company list would only answer 404 now.
  return useCompaniesMutation(
    (id: number) => client.deleteCompanyClass(id),
    (id) => queryKeys.classCompanies(id),
  );
}

export interface AssignCompaniesInput {
  items: EmployerRef[];
  classId: number | null;
}

export function useAssignCompanies(): UseMutationResult<void, Error, AssignCompaniesInput> {
  const client = useVidarioClient();
  return useCompaniesMutation((input: AssignCompaniesInput) => client.assignCompanies(input));
}

export interface MergeCompaniesInput {
  items: EmployerRef[];
  main: EmployerRef;
}

export function useMergeCompanies(): UseMutationResult<{ companyId: number; name: string }, Error, MergeCompaniesInput> {
  const client = useVidarioClient();
  return useCompaniesMutation((input: MergeCompaniesInput) => client.mergeCompanies(input));
}
